<template>
  <div :id="entity + '-collection'">
    <HostCollectionFilterForm v-if="entity === 'host'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></HostCollectionFilterForm>
    <DomainCollectionFilterForm v-if="entity === 'domain'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></DomainCollectionFilterForm>
    <ContactCollectionFilterForm v-if="entity === 'contact'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></ContactCollectionFilterForm>
    <AlphaTLDCollectionFilterForm v-if="entity === 'alpha'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></AlphaTLDCollectionFilterForm>
    <RegistrarCollectionFilterForm v-if="entity === 'registrar'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></RegistrarCollectionFilterForm>
    <TLDCollectionFilterForm v-if="entity === 'tld'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></TLDCollectionFilterForm>
    <TransferCollectionFilterForm v-if="entity === 'transfer'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></TransferCollectionFilterForm>
    <LogCollectionFilterForm v-if="entity === 'log'" :value="filter" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></LogCollectionFilterForm>
    <table class="unstriped hover">
      <thead>
      <tr>
        <th v-for="header in headers" :key="header.label">
          <template v-if="header.sortable">
            <a @click.prevent="sortBy(header.sort)">{{ header.label|t }} <i v-if="sort_by === header.sort" :class="sortingIconClass"></i></a>
          </template>
          <template v-else>
            {{ header.label|t }}
          </template>
        </th>
        <th v-if="actions">{{ 'Actions'|t }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="rec in results" :key="rec.id">
        <td v-for="(row, delta) in cols" :class="sort_by === headers[delta].sort ? 'sorting-col' : null">
          <template v-if="row.component"><component :is="row.component" v-bind="row.componentProps(rec) || {}" @submit="submitFilter" @change="changeFilter" @reset="resetFilter"></component></template>
          <template v-else-if="row.html"><span v-html="row.html(rec)"></span></template>
          <template v-else-if="row.flag"><flag :iso="rec[row.field]"></flag></template>
          <template v-else-if="row.date">{{ rec[row.field]|date(row.date) }}</template>
          <template v-else-if="row.timestamp">{{ rec[row.field]|timestamp(row.timestamp) }}</template>
          <template v-else>{{ rec[row.field] }}</template>
        </td>
        <td v-if="actions">
          <nav class="menu inline">
            <router-link v-for="action in actions(rec)" :key="action.name" :to="{name: action.name, params: action.params}" tag="li"><a><i :class="'icon icon-' + action.icon"></i> <span>{{ action.label|t }}</span></a></router-link>
          </nav>
        </td>
      </tr>
      </tbody>
    </table>
    <div class="expanded row">
      <div class="small-3 columns">{{ 'Total'|t }}: {{ total }}</div>
      <div class="small-7 columns"><Pager :total="totalPages()" :current="page" v-on:pager-go="goToPage"></Pager></div>
      <div class="small-2 columns">
        <div class="input-group">
          <span class="input-group-label"><label for="limit" class="inline">{{ 'Limit'|t }}:</label></span>
          <select class="input-group-field" id="limit" :value="limit" v-on:change.stop="changeLimit">
            <option v-for="i in [15, 25, 50, 75, 100]" :key="i" :value="i">{{ i }}</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Pager from '@/components/Pager';
  import api from '@/api';
  import HostCollectionFilterForm from '@/components/HostCollectionFilterForm';
  import DomainCollectionFilterForm from '@/components/DomainCollectionFilterForm';
  import ContactCollectionFilterForm from '@/components/ContactCollectionFilterForm';
  import AlphaTLDCollectionFilterForm from '@/components/AlphaTLDCollectionFilterForm';
  import RegistrarCollectionFilterForm from '@/components/RegistrarCollectionFilterForm';
  import TLDCollectionFilterForm from '@/components/TLDCollectionFilterForm';
  import TransferCollectionFilterForm from '@/components/TransferCollectionFilterForm';
  import LogCollectionFilterForm from '@/components/LogCollectionFilterForm';

  export default {
    name: 'Collection',
    components: {
      Pager,
      HostCollectionFilterForm,
      DomainCollectionFilterForm,
      ContactCollectionFilterForm,
      AlphaTLDCollectionFilterForm,
      RegistrarCollectionFilterForm,
      TLDCollectionFilterForm,
      TransferCollectionFilterForm,
      LogCollectionFilterForm
    },
    props: {
      handler: {
        type: String,
        required: true
      },
      entity: {
        type: String,
        required: true
      },
      cols: {
        type: Array,
        required: true
      },
      actions: {
        type: Function,
        require: false
      }
    },
    data() {
      return {
        total: 0,
        page: 0,
        limit: 25,
        results: [],
        filter: {},
        send_filter: false,
        sort_by: "",
        sort_order: "",
        sort_history: [],
        sortables: []
      }
    },
    computed: {
      sortingIconClass() {
        return 'icon icon-sort-amount-' + (this.sort_order === 'desc' ? 'desc' : 'asc');
      },
      headers() {
        let headers = this.cols;
        for (let i in headers) {
          headers[i].sortable = headers[i].hasOwnProperty("sort") && this.sortables.indexOf(headers[i].sort) > -1;
        }
        return headers;
      }
    },
    created() {
      this.fetch(this.page, this.limit);
    },
    methods: {
      totalPages() {
        return Math.ceil(this.total / this.limit)
      },
      goToPage(n) {
        this.fetch(n, this.limit);
      },
      changeLimit(event) {
        this.fetch(0, parseInt(event.target.value));
      },
      fetch(page, limit, sort) {
        let request = { page: page, limit: limit };

        if (this.send_filter) {
          request.filter = this.filter
        }

        if (sort) {
          request.sort_by = sort;
        } else if (this.sort_by !== "") {
          request.sort_by = this.sort_by + (this.sort_order === "desc" ? ":desc" : ":asc");
        }

        api[this.handler](
          request,
          data => {
            this.total = data.total;
            this.page = data.page;
            this.limit = data.limit;
            this.results = data.results;
            this.filter = this.send_filter ? (data.filter || {}) : {};
            this.sortables = data.sortable || [];

            if (data.hasOwnProperty("sorted_by") && typeof data.sorted_by === "string" && data.sorted_by.length > 0) {
              let sort = data.sorted_by.split(":");
              this.sort_by = sort.length > 0 ? sort[0] : "";
              this.sort_order = sort.length > 1 ? sort[1] : "";
              if (this.sort_by !== this.sort_history.slice(-1)[0]) {
                this.sort_history = [];
              }
            } else {
              this.sort_by = "";
              this.sort_order = "";
              this.sort_history = [];
            }
          }
        );
      },
      submitFilter(values) {
        this.filter = values;
        this.send_filter = true;
        this.goToPage(0)
      },
      changeFilter(values) {
        this.filter = values;
        this.send_filter = true;
      },
      resetFilter(values) {
        this.send_filter = false;
        this.filter = {};
        this.fetch(0, 25);
      },
      sortBy(col) {
        this.sort_history.push(col);
        if (this.sort_history.slice(-3).join("") === col.repeat(3)) {
          this.sort_history = [];
          this.sort_by = "";
          this.sort_order = "";
          this.fetch(0, 25);
          return;
        }
        col += (this.sort_by === col && this.sort_order === "asc" ? ":desc" : ":asc");
        this.fetch(0, 25, col);
      },
    },
  }
</script>



// WEBPACK FOOTER //
// src/components/Collection.vue